import React, { useEffect, useMemo, useRef, useState } from "react"

import { CalendarOutlined } from "@ant-design/icons"
import { DatePicker } from "antd"
import moment, { type Moment } from "moment-timezone"
import type {
    DeepMap,
    FieldError,
    FieldValues,
    UseFormRegister,
    UseFormSetValue,
    UseFormUnregister,
} from "react-hook-form"

import type { IIndexable } from "@common-shared/types/general.types"

import { FRONT_GENERAL_CONFIG } from "../config/general.config"
import { NIRVANA_COLORS } from "../config/theme.config"
import { UtilHelper } from "../helpers/util.helper"
import { FloatingLabelElement } from "./floatingLabel.element"
import { InputErrorElement } from "./inputError.element"

export const DatePickerElement: React.FunctionComponent<
    IDatePickerElementProps
> = props => {
    const [instanceIdentifierClassName] = useState(
        `date-picker-${UtilHelper.getRandomInt()}-${new Date().getTime()}`,
    )
    const [defaultProcessed, setDefaultProcessed] = useState<boolean>(false)
    const [isFocused, setIsFocused] = useState<boolean>(false)

    const valueRef = useRef<Moment | undefined>(undefined)

    const elementFullName = useMemo<string>(() => {
        return `${props.fieldGroupName ? `${props.fieldGroupName}.` : ""}${
            props.name
        }`
    }, [props.name, props.fieldGroupName])

    const isLabelFloated = useMemo<boolean>(() => {
        return props.isLabelStatic || !!valueRef.current || isFocused
    }, [valueRef.current, isFocused, props.isLabelStatic])

    const inputHasError = useMemo<boolean>(() => {
        return !!props.reactHookFormErrors?.[props.name]
    }, [props.reactHookFormErrors?.[props.name]])

    // Set react hook form handlers
    useEffect(() => {
        if (props.reactHookFormRegister) {
            props.reactHookFormRegister(
                elementFullName,
                props.reactHookFormValidations || {},
            )
        }

        return () => {
            props.reactHookFormUnregister?.(elementFullName)
        }
    }, [props.reactHookFormRegister])

    // Process default value
    useEffect(() => {
        if (!props.defaultValue || defaultProcessed) {
            return
        }

        setDefaultProcessed(true)
        onChange(props.defaultValue)
    }, [props.defaultValue])

    // Set listener on picker's input, so once you just enter with keyboard and then click outside, value remains
    useEffect(() => {
        const querySelector = `.${instanceIdentifierClassName} .ant-picker-input input`
        const [dateInput] = Array.from(document.querySelectorAll(querySelector))

        const handlePickerInputChange = () => {
            if (
                !dateInput ||
                // Accepted formats currently span 8-10 characters; e.g., "MMDDYYYY" (8), "MM/D/YYYY" (9), and "MM/DD/YYYY" (10)
                // @ts-ignore
                !(dateInput.value.length >= 8 && dateInput.value.length <= 10)
            ) {
                return
            }

            const dateObject = moment(
                // @ts-ignore
                dateInput.value,
                [
                    FRONT_GENERAL_CONFIG.unpunctuatedMomentDateFormat,
                    FRONT_GENERAL_CONFIG.defaultSimplifiedMomentDateFormat,
                    FRONT_GENERAL_CONFIG.secondarySimplifiedMomentDateFormat,
                    FRONT_GENERAL_CONFIG.tertiarySimplifiedMomentDateFormat,
                    FRONT_GENERAL_CONFIG.defaultMomentDateFormat,
                    FRONT_GENERAL_CONFIG.secondaryMomentDateFormat,
                    FRONT_GENERAL_CONFIG.mysqlMomentDateFormat,
                ],
                true,
            )

            if (dateObject.isValid()) {
                onChange(dateObject)
            }
        }

        if (dateInput) {
            dateInput.addEventListener("keyup", handlePickerInputChange)
        }

        return () => {
            dateInput.removeEventListener("keyup", handlePickerInputChange)
        }
    }, [instanceIdentifierClassName])

    const onChange = (value: Moment | null) => {
        if (
            (!value && !props.allowClear) ||
            (value &&
                (!value.isValid() || valueRef.current?.unix() === value.unix()))
        ) {
            return
        }

        if (props.onChange) {
            props.onChange(value || undefined)
        }

        if (props.reactHookFormSet) {
            props.reactHookFormSet(elementFullName, value, {
                shouldValidate: !!props.shouldValidate,
            })
        }

        valueRef.current = value || undefined
    }

    return (
        <div
            className={`
                relative w-full
                ${props.className}
            `}
        >
            {props.label && (
                <FloatingLabelElement
                    label={props.label}
                    isFloated={isLabelFloated}
                    withRequiredStar={
                        props.withRequiredStar &&
                        props.reactHookFormValidations?.required
                    }
                    tooltip={props.tooltip}
                />
            )}

            <DatePicker
                className={`
                    w-full
                    ${instanceIdentifierClassName}
                    ${inputHasError ? "errored" : ""}
                `}
                value={valueRef.current}
                format={FRONT_GENERAL_CONFIG.defaultMomentDateFormat}
                allowClear={props.allowClear}
                suffixIcon={
                    <CalendarOutlined
                        style={{
                            color: NIRVANA_COLORS.brand.primary,
                            fontSize: 16,
                        }}
                    />
                }
                size={props.size || "large"}
                defaultValue={props.defaultValue}
                // @ts-ignore
                onChange={onChange}
                disabled={props.disabled}
                placeholder={
                    isLabelFloated || !props.label
                        ? props.placeholder ||
                          FRONT_GENERAL_CONFIG.defaultMomentDateFormat
                        : ""
                }
                onFocus={() => {
                    setIsFocused(true)
                }}
                onBlur={() => {
                    setIsFocused(false)
                }}
                popupStyle={{
                    zIndex: props.pickerZIndex,
                }}
                disabledDate={(currentDate: Moment) =>
                    !props.minDate && !props.maxDate
                        ? false
                        : (!!props.minDate &&
                              moment(props.minDate).isAfter(
                                  currentDate,
                                  "day",
                              )) ||
                          (!!props.maxDate &&
                              moment(props.maxDate).isBefore(
                                  currentDate,
                                  "day",
                              ))
                }
            />

            {props.reactHookFormErrors?.[props.name] && (
                <InputErrorElement
                    text={
                        (props.reactHookFormErrors[props.name] as FieldError)
                            .message || "Error"
                    }
                />
            )}
        </div>
    )
}

interface IDatePickerElementProps {
    className?: string
    name: string
    fieldGroupName?: string
    label?: string
    placeholder?: string
    isLabelStatic?: boolean
    withRequiredStar?: boolean
    tooltip?: string
    defaultValue?: Moment
    size?: "small" | "middle" | "large"
    allowClear?: boolean
    reactHookFormValidations?: IIndexable
    reactHookFormRegister?: UseFormRegister<FieldValues>
    reactHookFormUnregister?: UseFormUnregister<FieldValues>
    reactHookFormSet?: UseFormSetValue<FieldValues>
    reactHookFormErrors?: DeepMap<FieldValues, FieldError>
    shouldValidate?: boolean
    disabled?: boolean
    onChange?: (newValue: Moment | undefined) => void
    minDate?: Date
    maxDate?: Date
    pickerZIndex?: number
}
